import React, { useMemo, useState, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import _get from 'lodash/get';
import { Attachment } from 'graphql-common';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import CarouselCommon from '@lib/components/Carousel/Carousel';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import styles from './AttachmentListPreview.module.scss';

type Props = {
  attachments?: (Attachment | null | undefined)[] | null;
  className?: string;
  isNavDisabled?: boolean;
};

type AttachmentItem = {
  attachment: Attachment;
  fileName: string;
  isImage: boolean;
  isPdf: boolean;
  isPreviewable: boolean;
  mimeType?: string | null;
  originalIndex: number;
  previewUrl?: string | null;
};

const getFileName = (attachment: Attachment) =>
  attachment.filename ||
  _get(attachment, ['fileData', 'data', 'metadata', 'filename']) ||
  'file';

const getMimeType = (attachment: Attachment) =>
  _get(attachment, ['fileData', 'data', 'metadata', 'mimeType']) ||
  _get(attachment, ['fileData', 'metadata', 'mimeType']);

const getPreviewUrl = (attachment: Attachment) =>
  _get(attachment, 'url') ||
  _get(attachment, ['urls', 'imagePreview']) ||
  _get(attachment, ['urls', 'small']) ||
  _get(attachment, ['fileData', 'url']);

export default function AttachmentListPreview(props: Props) {
  const { attachments, className, isNavDisabled } = props;

  const filteredAttachments = useMemo(
    () =>
      Array.isArray(attachments)
        ? (attachments.filter((item): item is Attachment =>
            Boolean(item && item.id),
          ) as Attachment[])
        : [],
    [attachments],
  );

  const attachmentItems = useMemo<AttachmentItem[]>(
    () =>
      filteredAttachments.map((attachment, index) => {
        const mimeType = getMimeType(attachment);
        const isImage = Boolean(mimeType && mimeType.includes('image'));
        const isPdf = Boolean(mimeType && mimeType.includes('pdf'));
        const previewUrl =
          isImage || isPdf ? getPreviewUrl(attachment) : undefined;

        return {
          attachment,
          originalIndex: index,
          fileName: getFileName(attachment),
          mimeType,
          previewUrl,
          isImage,
          isPdf,
          isPreviewable: Boolean(previewUrl),
        };
      }),
    [filteredAttachments],
  );

  const previewableItems = useMemo(
    () => attachmentItems.filter(({ isPreviewable }) => isPreviewable),
    [attachmentItems],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);

  useEffect(() => {
    if (
      previewableItems.length &&
      activePreviewIndex > previewableItems.length - 1
    ) {
      setActivePreviewIndex(previewableItems.length - 1);
    }
  }, [previewableItems.length, activePreviewIndex]);

  useEffect(() => {
    if (!previewableItems.length && isModalOpen) {
      setIsModalOpen(false);
    }
  }, [previewableItems.length, isModalOpen]);

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleOpen = useCallback(
    (itemIndex: number) => {
      const previewIndex = previewableItems.findIndex(
        ({ originalIndex }) => originalIndex === itemIndex,
      );
      if (previewIndex === -1) return;
      setActivePreviewIndex(previewIndex);
      setIsModalOpen(true);
    },
    [previewableItems],
  );

  const hasNavigation = previewableItems.length > 1;
  const isPrevDisabled = activePreviewIndex === 0;
  const isNextDisabled =
    previewableItems.length === 0 ||
    activePreviewIndex === previewableItems.length - 1;

  const handlePrev = useCallback(() => {
    setActivePreviewIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const handleNext = useCallback(() => {
    setActivePreviewIndex((prev) =>
      prev < previewableItems.length - 1 ? prev + 1 : prev,
    );
  }, [previewableItems.length]);

  const activeAttachment = previewableItems[activePreviewIndex];

  const renderedAttachments = useMemo<React.ReactNode[]>(
    () =>
      attachmentItems.map((item) => {
        const { attachment: currentAttachment, originalIndex } = item;
        return (
          <DocumentationPreview
            key={uuidv4()}
            attachment={currentAttachment}
            view={DocumentationPreviewEnum.BlockCard}
            onClick={() => handleOpen(originalIndex)}
          />
        );
      }),
    [attachmentItems, handleOpen],
  );

  if (!attachmentItems.length) {
    return null;
  }

  const swiperProps = isNavDisabled
    ? undefined
    : {
        onPrevious: hasNavigation ? handlePrev : undefined,
        onNext: hasNavigation ? handleNext : undefined,
        isPreviousDisabled: !hasNavigation || isPrevDisabled,
        isNextDisabled: !hasNavigation || isNextDisabled,
      };

  return (
    <>
      <div className={classNames(styles.wrapper, className)}>
        <CarouselCommon
          key={`attachments-${attachmentItems.length}`}
          withWrap
          spaceBetween={16}
          slidesPerView="auto"
        >
          {renderedAttachments}
        </CarouselCommon>
      </div>
      {isModalOpen && activeAttachment && (
        <FileViewerModal
          attachment={activeAttachment.attachment}
          attachmentName={activeAttachment.fileName}
          isOpen={isModalOpen}
          onClose={handleClose}
          swiperProps={swiperProps}
        />
      )}
    </>
  );
}
